/**
 * @bos/portal3 — the ADMIN area (route `/admin`, inside <AppShell>). Three jobs, in the
 * order they matter: who has an account and what they may do; what people actually spent;
 * which models their runs are pointed at.
 *
 * THE GUARD IS THE SERVER'S: every `admin.*` procedure refuses a non-admin (FORBIDDEN)
 * before any resolver runs, and every rule this screen appears to enforce — the last
 * administrator, self-suspension, the cross-model critic union — is enforced there too.
 * This page checks `user.role` locally and disables the buttons a rule would refuse, but
 * only as a courtesy: to say why in calm words instead of firing a request that fails.
 * Nothing here grants anything, and nothing here is the check.
 *
 * UNITS, HONESTLY: everything monetary on this screen is CREDITS — the product's own unit,
 * the one an admin hands out. No money figure is derived anywhere: what a credit costs
 * depends on which providers a run ends up using, which is not known in advance. The peg
 * (`settings/store.ts` → `creditsToTokens`, currently 1 credit = 1 token) lives on the
 * server; this screen never converts.
 *
 * SPEND IS SHOWN EXACTLY AS FAR AS THE BYTES GO. `admin.spend` reads each run's own
 * `usage-rollup.json`; where a run has no roll-up, or one that predates the completeness
 * flag, or one that declares itself incomplete, this screen SAYS SO instead of folding the
 * gap into a confident number. A total built over unmeasured runs would rebuild the exact
 * lie the ledger work removed, one layer up.
 */
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { TRPCClientError } from '@trpc/client';
import { PageHeader } from '../components/PageHeader';
import { useAuth } from '../auth/AuthContext';
import { client } from '../rpc/client';

// ── the wire shapes (structurally mirrored from the server's result types) ───────────────

type Role = 'user' | 'admin';
type Status = 'active' | 'suspended';

interface WireUser {
  readonly id: string;
  readonly email: string;
  readonly displayName: string;
  readonly role: Role;
  readonly status: Status;
  readonly createdAt: string;
}

interface WireModelSelection {
  readonly think: string | null;
  readonly emit: string | null;
  readonly criticB: string | null;
}

interface WireStoredModels {
  readonly selection: WireModelSelection;
  readonly mayChooseModels: boolean;
  readonly criticCollapseAck: boolean;
  readonly setBy: 'admin' | 'account' | null;
  readonly updatedAt: string | null;
}

interface WireKnownModel {
  readonly spec: string;
  readonly provider: string;
  readonly label: string;
  readonly priced: boolean;
}

interface AdminUsers {
  readonly users: readonly {
    readonly user: WireUser;
    readonly maxRunCredits: number | null;
    readonly models: WireStoredModels;
    readonly liveSessions: number;
  }[];
  readonly defaultMaxRunCredits: number;
  readonly activeAdmins: number;
  readonly callerId: string;
  readonly chainProviders: readonly string[];
  readonly knownModels: readonly WireKnownModel[];
}

type Completeness = 'complete' | 'incomplete' | 'unknown' | 'absent' | 'unreadable';

interface WireUserSpend {
  readonly userId: string;
  readonly runs: number;
  readonly runsMeasured: number;
  readonly runsWithoutRecord: number;
  readonly runsUnreadable: number;
  readonly runsUnknownCompleteness: number;
  readonly runsIncomplete: number;
  readonly credits: number;
  readonly complete: boolean;
  readonly range: { readonly minCredits: number; readonly maxCredits: number } | null;
}

interface AdminSpend {
  readonly rows: readonly { readonly user: WireUser; readonly spend: WireUserSpend }[];
  readonly total: WireUserSpend;
}

interface WireRunSpend {
  readonly runId: string;
  readonly completeness: Completeness;
  readonly credits: number | null;
  readonly tokensComplete: boolean;
  readonly range: { readonly minCredits: number; readonly maxCredits: number } | null;
  /** The server's own sentences, in credits. Never the factory's note prose — that quotes euros. */
  readonly reasons: readonly string[];
  readonly unpricedModels: readonly string[];
  readonly measuredAt: string | null;
}

interface AdminUserSpend {
  readonly user: WireUser;
  readonly runs: readonly {
    readonly run: {
      readonly id: string;
      readonly projectName: string;
      readonly appName: string | null;
      readonly status: string;
      readonly startedAt: string;
    };
    readonly spend: WireRunSpend;
  }[];
  readonly spend: WireUserSpend;
}

function errorMessage(err: unknown): string {
  if (err instanceof TRPCClientError) return err.message || 'Something went wrong.';
  return 'Could not reach the server. Please try again.';
}

/** "20,000,000" — a plain grouped number; the unit is written out beside it, once. */
function formatCredits(n: number): string {
  return n.toLocaleString('en-US');
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

// ══ SECTION 1 — PEOPLE ═══════════════════════════════════════════════════════════════════

interface PersonRowProps {
  readonly row: AdminUsers['users'][number];
  readonly data: AdminUsers;
  readonly reload: () => Promise<void>;
}

function PersonRow({ row, data, reload }: PersonRowProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [editingLimit, setEditingLimit] = useState(false);
  const [draft, setDraft] = useState('');

  const u = row.user;
  const isSelf = u.id === data.callerId;
  const hasOverride = row.maxRunCredits !== null;
  const suspended = u.status === 'suspended';
  // Mirror of the server's `wouldRemoveLastAdmin`: an active admin who is the only one.
  const isLastAdmin = u.role === 'admin' && u.status === 'active' && data.activeAdmins <= 1;

  async function act(fn: () => Promise<string | null>) {
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const said = await fn();
      setNote(said);
      await reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function onSaveLimit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parsed = Number(draft.replaceAll(',', '').trim());
    if (!Number.isInteger(parsed) || parsed < 1) {
      setError('The limit must be a whole number of credits, at least 1.');
      return;
    }
    void act(async () => {
      await client.admin.setRunLimit.mutate({ userId: u.id, maxRunCredits: parsed });
      setEditingLimit(false);
      setDraft('');
      return null;
    });
  }

  return (
    <li className="prow" data-testid={`person-${u.email}`}>
      <div className="prow__who">
        <span className="prow__name">
          {u.displayName}
          {isSelf && <span className="prow__you"> (you)</span>}
          {u.role === 'admin' && <span className="badge badge--run-idle prow__badge">admin</span>}
          {suspended && <span className="badge badge--run-failed prow__badge">suspended</span>}
        </span>
        <span className="prow__meta">
          {u.email} · joined {formatDate(u.createdAt)} ·{' '}
          {row.liveSessions === 0
            ? 'not signed in anywhere'
            : `${row.liveSessions} live session${row.liveSessions === 1 ? '' : 's'}`}
        </span>
        <span className="prow__meta">
          {hasOverride
            ? `Limit: ${formatCredits(row.maxRunCredits as number)} credits per run — set for this person`
            : `Limit: ${formatCredits(data.defaultMaxRunCredits)} credits per run — the system default`}
        </span>
      </div>

      <div className="prow__actions">
        {!editingLimit ? (
          <>
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy}
              onClick={() => {
                setEditingLimit(true);
                setError(null);
                setDraft(hasOverride ? String(row.maxRunCredits) : '');
              }}
            >
              Change limit
            </button>
            {hasOverride && (
              <button
                className="btn btn--quiet btn--sm"
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await client.admin.setRunLimit.mutate({ userId: u.id, maxRunCredits: null });
                    return null;
                  })
                }
              >
                Use the default
              </button>
            )}

            {u.role === 'user' ? (
              <button
                className="btn btn--quiet btn--sm"
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await client.admin.setRole.mutate({ userId: u.id, role: 'admin' });
                    return `${u.displayName} is now an administrator.`;
                  })
                }
              >
                Make administrator
              </button>
            ) : (
              <button
                className="btn btn--quiet btn--sm"
                type="button"
                disabled={busy || isLastAdmin}
                title={
                  isLastAdmin
                    ? 'This is the only administrator who can sign in. Promote someone else first.'
                    : undefined
                }
                onClick={() =>
                  void act(async () => {
                    const r = (await client.admin.setRole.mutate({
                      userId: u.id,
                      role: 'user',
                    })) as { selfDemoted: boolean };
                    return r.selfDemoted
                      ? 'You are no longer an administrator. Your other sessions were signed out, and this screen will refuse from here on.'
                      : `${u.displayName} is no longer an administrator, and their sessions were signed out.`;
                  })
                }
              >
                Remove administrator
              </button>
            )}

            {!suspended ? (
              <button
                className="btn btn--quiet btn--sm"
                type="button"
                disabled={busy || isSelf || isLastAdmin}
                title={
                  isSelf
                    ? 'You cannot suspend your own account.'
                    : isLastAdmin
                      ? 'This is the only administrator who can sign in.'
                      : undefined
                }
                onClick={() =>
                  void act(async () => {
                    const r = (await client.admin.setStatus.mutate({
                      userId: u.id,
                      status: 'suspended',
                    })) as { sessionsRevoked: number };
                    return r.sessionsRevoked === 0
                      ? `${u.displayName} is suspended. They had no session open; they cannot sign in.`
                      : `${u.displayName} is suspended. ${r.sessionsRevoked} session${
                          r.sessionsRevoked === 1 ? ' was' : 's were'
                        } signed out, and they cannot sign in again.`;
                  })
                }
              >
                Suspend
              </button>
            ) : (
              <button
                className="btn btn--ghost btn--sm"
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(async () => {
                    await client.admin.setStatus.mutate({ userId: u.id, status: 'active' });
                    return `${u.displayName} can sign in again. They will need to, because suspending signed them out everywhere.`;
                  })
                }
              >
                Restore
              </button>
            )}
          </>
        ) : (
          <form className="prow__form" onSubmit={onSaveLimit}>
            <input
              className="form__input prow__input"
              inputMode="numeric"
              pattern="[0-9,]*"
              placeholder={`e.g. ${formatCredits(data.defaultMaxRunCredits)}`}
              aria-label={`Per-run credit limit for ${u.email}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={busy}
            />
            <button className="btn btn--primary btn--sm" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Set limit'}
            </button>
            <button
              className="btn btn--quiet btn--sm"
              type="button"
              disabled={busy}
              onClick={() => {
                setEditingLimit(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </form>
        )}
      </div>

      {error !== null && (
        <p className="form__error prow__wide" role="alert">
          {error}
        </p>
      )}
      {note !== null && (
        <p className="prow__note prow__wide" role="status">
          {note}
        </p>
      )}
    </li>
  );
}

function CreatePerson({ reload }: { readonly reload: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>('user');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await client.admin.createUser.mutate({ email, displayName, password, role });
      setNote(
        `Account created for ${email}. There is no email in this product — give them the password yourself.`,
      );
      setEmail('');
      setDisplayName('');
      setPassword('');
      setRole('user');
      setOpen(false);
      await reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="admin-create">
      {!open ? (
        <button className="btn btn--ghost btn--sm" type="button" onClick={() => setOpen(true)}>
          Add a person
        </button>
      ) : (
        <form className="form admin-create__form" onSubmit={submit}>
          <div className="form__row">
            <label className="form__label" htmlFor="new-name">
              Name
              <input
                id="new-name"
                className="form__input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                disabled={busy}
                required
              />
            </label>
            <label className="form__label" htmlFor="new-email">
              Email
              <input
                id="new-email"
                className="form__input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                required
              />
            </label>
          </div>
          <div className="form__row">
            <label className="form__label" htmlFor="new-password">
              Password
              <input
                id="new-password"
                className="form__input"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                required
              />
            </label>
            <label className="form__label" htmlFor="new-role">
              Role
              <select
                id="new-role"
                className="form__input"
                value={role}
                onChange={(e) => setRole(e.target.value as Role)}
                disabled={busy}
              >
                <option value="user">Person</option>
                <option value="admin">Administrator</option>
              </select>
            </label>
          </div>
          <p className="form__hint">
            You choose the password and hand it over yourself. Sorvel sends no email, so there is
            no invitation and no reset link — if it is lost, an administrator sets a new one.
          </p>
          <div className="prow__actions">
            <button className="btn btn--primary btn--sm" type="submit" disabled={busy}>
              {busy ? 'Creating…' : 'Create account'}
            </button>
            <button
              className="btn btn--quiet btn--sm"
              type="button"
              disabled={busy}
              onClick={() => {
                setOpen(false);
                setError(null);
              }}
            >
              Cancel
            </button>
          </div>
          {error !== null && (
            <p className="form__error" role="alert">
              {error}
            </p>
          )}
        </form>
      )}
      {note !== null && (
        <p className="prow__note" role="status">
          {note}
        </p>
      )}
    </div>
  );
}

// ══ SECTION 2 — SPEND ════════════════════════════════════════════════════════════════════

/**
 * The one sentence that decides whether a figure may be read as a total. It is written in
 * one place so the roster line, the per-run line and the headline cannot drift apart —
 * and so that every path through it names the reason rather than hedging generically.
 */
function completenessSentence(s: WireUserSpend): string {
  if (s.runs === 0) return 'No runs yet.';
  if (s.complete) {
    return `Every one of the ${s.runs} run${s.runs === 1 ? '' : 's'} left a complete record.`;
  }
  const gaps: string[] = [];
  if (s.runsWithoutRecord > 0) {
    gaps.push(
      `${s.runsWithoutRecord} run${s.runsWithoutRecord === 1 ? '' : 's'} left no spend record at all`,
    );
  }
  if (s.runsUnreadable > 0) {
    gaps.push(
      `${s.runsUnreadable} record${s.runsUnreadable === 1 ? '' : 's'} could not be read`,
    );
  }
  if (s.runsUnknownCompleteness > 0) {
    gaps.push(
      `${s.runsUnknownCompleteness} record${
        s.runsUnknownCompleteness === 1 ? ' predates' : 's predate'
      } the completeness check`,
    );
  }
  if (s.runsIncomplete > 0) {
    gaps.push(
      `${s.runsIncomplete} record${s.runsIncomplete === 1 ? '' : 's'} report${
        s.runsIncomplete === 1 ? 's' : ''
      } itself incomplete`,
    );
  }
  return `This is a floor, not a total — ${gaps.join('; ')}.`;
}

/** The credit figure, said the only way the bytes allow. */
function creditsSentence(s: WireUserSpend): string {
  if (s.runsMeasured === 0) {
    return s.runs === 0 ? 'Nothing spent' : 'Nothing measurable';
  }
  if (s.range !== null && s.range.minCredits !== s.range.maxCredits) {
    return `Between ${formatCredits(s.range.minCredits)} and ${formatCredits(
      s.range.maxCredits,
    )} credits`;
  }
  return `${formatCredits(s.credits)}${s.complete ? '' : '+'} credits`;
}

function RunSpendLine({ row }: { readonly row: AdminUserSpend['runs'][number] }) {
  const { run, spend } = row;
  const what = run.appName ?? run.projectName;

  // THE FIGURE. Each branch says only what its own state supports: no record means no
  // number at all (not zero — zero is a measurement), an un-summable remainder means the
  // interval rather than a pick, and a record that never claimed completeness gets a "+".
  let figure: string;
  switch (spend.completeness) {
    case 'absent':
      figure = 'no record';
      break;
    case 'unreadable':
      figure = 'unreadable';
      break;
    case 'unknown':
      figure = `${formatCredits(spend.credits ?? 0)}+ credits`;
      break;
    case 'incomplete':
      figure =
        spend.range !== null
          ? `between ${formatCredits(spend.range.minCredits)} and ${formatCredits(
              spend.range.maxCredits,
            )} credits`
          : `${formatCredits(spend.credits ?? 0)} credits`;
      break;
    default:
      figure = `${formatCredits(spend.credits ?? 0)} credits`;
  }

  return (
    <li className="srun" data-testid={`run-spend-${run.id}`}>
      <div className="srun__head">
        <span className="srun__what">{what}</span>
        <span className="srun__figure">{figure}</span>
      </div>
      <div className="srun__meta">
        {run.status} · started {formatDate(run.startedAt)}
        {run.appName === null && ' · built before apps existed'}
      </div>
      {/* The server's sentences, in credits — see admin/spend.ts for why they are not the
          factory's own note prose (that quotes euro amounts inside the sentence). */}
      {spend.reasons.map((n, i) => (
        <p className="srun__why" key={i}>
          {n}
        </p>
      ))}
    </li>
  );
}

function SpendSection() {
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdminSpend | null>(null);
  const [openUser, setOpenUser] = useState<string | null>(null);
  const [detail, setDetail] = useState<AdminUserSpend | null>(null);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      setData((await client.admin.spend.query()) as AdminSpend);
      setStatus('ready');
    } catch (err) {
      setError(errorMessage(err));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const openDetail = useCallback(async (userId: string) => {
    setOpenUser(userId);
    setDetail(null);
    try {
      setDetail((await client.admin.userSpend.query({ userId })) as AdminUserSpend);
    } catch {
      setDetail(null);
    }
  }, []);

  if (status === 'loading') return <p className="page__note">Reading the run records…</p>;
  if (status === 'error' || data === null) {
    return (
      <div className="projects__state">
        <p className="form__error" role="alert">
          {error ?? 'We couldn’t read the spend records.'}
        </p>
        <button className="btn btn--ghost" type="button" onClick={() => void load()}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="card">
      <h2 className="card__title">What has been spent</h2>
      <p className="card__intro">
        Read from each run’s own usage record on disk, in credits. Where a run left no record,
        or a record that cannot say whether it is whole, that is said here instead of being
        folded into the number — a total over unmeasured runs would be a guess wearing a
        total’s clothes.
      </p>

      <div className="spend-total" data-testid="spend-total">
        <span className="spend-total__figure">{creditsSentence(data.total)}</span>
        <span className="spend-total__why">{completenessSentence(data.total)}</span>
      </div>

      <ul className="spend-list">
        {data.rows.map(({ user, spend }) => (
          <li className="srow" key={user.id} data-testid={`spend-${user.email}`}>
            <div className="srow__who">
              <span className="prow__name">{user.displayName}</span>
              <span className="prow__meta">{user.email}</span>
              <span className="prow__meta">{completenessSentence(spend)}</span>
            </div>
            <span className="srow__figure">{creditsSentence(spend)}</span>
            <button
              className="btn btn--quiet btn--sm"
              type="button"
              onClick={() =>
                openUser === user.id ? setOpenUser(null) : void openDetail(user.id)
              }
              disabled={spend.runs === 0}
            >
              {openUser === user.id ? 'Hide runs' : `${spend.runs} run${spend.runs === 1 ? '' : 's'}`}
            </button>
            {openUser === user.id && (
              <div className="srow__detail">
                {detail === null ? (
                  <p className="page__note">Reading…</p>
                ) : (
                  <ul className="srun-list">
                    {detail.runs.map((r) => (
                      <RunSpendLine row={r} key={r.run.id} />
                    ))}
                  </ul>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ══ SECTION 3 — MODELS ═══════════════════════════════════════════════════════════════════

const ROLE_LABEL: Readonly<Record<keyof WireModelSelection, string>> = {
  think: 'Thinker',
  emit: 'Writer',
  criticB: 'Second critic',
};

const ROLE_WHY: Readonly<Record<keyof WireModelSelection, string>> = {
  think: 'Every reasoning stage of the architect chain.',
  emit: 'The transcription roles — emit, repair, enrich. No judgment.',
  criticB:
    'The second arm of every stage’s critic union. It must not be the thinker’s provider — that is the guard below.',
};

function describeSelection(m: WireStoredModels): string {
  const s = m.selection;
  if (s.think === null && s.emit === null && s.criticB === null) {
    return 'No selection — the factory’s own configuration decides.';
  }
  const parts: string[] = [];
  if (s.think !== null) parts.push(`thinker ${s.think}`);
  if (s.emit !== null) parts.push(`writer ${s.emit}`);
  if (s.criticB !== null) parts.push(`second critic ${s.criticB}`);
  const by = m.setBy === 'account' ? 'chosen by the account' : 'set by an administrator';
  return `${parts.join(' · ')} — ${by}`;
}

interface ModelRowProps {
  readonly row: AdminUsers['users'][number];
  readonly data: AdminUsers;
  readonly reload: () => Promise<void>;
}

function ModelRow({ row, data, reload }: ModelRowProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [think, setThink] = useState(row.models.selection.think ?? '');
  const [emit, setEmit] = useState(row.models.selection.emit ?? '');
  const [criticB, setCriticB] = useState(row.models.selection.criticB ?? '');
  const [ack, setAck] = useState(false);

  const u = row.user;

  // The same grade the server computes, so the form can warn BEFORE the request. The server
  // still decides — this only shortens the loop.
  const collapse = useMemo(() => {
    if (think.trim() === '' || criticB.trim() === '') return null;
    const p = (s: string) => {
      const i = s.indexOf(':');
      return i > 0
        ? { provider: s.slice(0, i).toLowerCase(), model: s.slice(i + 1).toLowerCase() }
        : { provider: '', model: s.toLowerCase() };
    };
    const a = p(think.trim());
    const b = p(criticB.trim());
    if (a.provider !== b.provider) return null;
    return a.model === b.model ? 'same-model' : 'same-provider';
  }, [think, criticB]);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setWarning(null);
    try {
      const r = (await client.admin.setModels.mutate({
        userId: u.id,
        selection: {
          think: think.trim() === '' ? null : think.trim(),
          emit: emit.trim() === '' ? null : emit.trim(),
          criticB: criticB.trim() === '' ? null : criticB.trim(),
        },
        allowSameProviderCritic: ack,
      })) as { warning: string | null };
      setWarning(r.warning);
      setOpen(false);
      await reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="prow" data-testid={`models-${u.email}`}>
      <div className="prow__who">
        <span className="prow__name">
          {u.displayName}
          {row.models.mayChooseModels && (
            <span className="badge badge--soon prow__badge">may choose</span>
          )}
          {row.models.criticCollapseAck && (
            <span className="badge badge--run-failed prow__badge">critic guard overridden</span>
          )}
        </span>
        <span className="prow__meta">{describeSelection(row.models)}</span>
      </div>

      <div className="prow__actions">
        <button
          className="btn btn--quiet btn--sm"
          type="button"
          disabled={busy}
          onClick={() =>
            void (async () => {
              setBusy(true);
              try {
                await client.admin.setMayChooseModels.mutate({
                  userId: u.id,
                  mayChooseModels: !row.models.mayChooseModels,
                });
                await reload();
              } catch (err) {
                setError(errorMessage(err));
              } finally {
                setBusy(false);
              }
            })()
          }
        >
          {row.models.mayChooseModels ? 'Take back the choice' : 'Let them choose'}
        </button>
        <button
          className="btn btn--ghost btn--sm"
          type="button"
          disabled={busy}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Cancel' : 'Set models'}
        </button>
      </div>

      {open && (
        <form className="form mform prow__wide" onSubmit={save}>
          {(['think', 'emit', 'criticB'] as const).map((role) => {
            const value = role === 'think' ? think : role === 'emit' ? emit : criticB;
            const set = role === 'think' ? setThink : role === 'emit' ? setEmit : setCriticB;
            return (
              <label className="form__label" key={role}>
                {ROLE_LABEL[role]}
                <input
                  className="form__input"
                  list="chain-models"
                  placeholder="leave empty — the factory decides"
                  aria-label={`${ROLE_LABEL[role]} model for ${u.email}`}
                  value={value}
                  onChange={(e) => set(e.target.value)}
                  disabled={busy}
                />
                <span className="form__hint">{ROLE_WHY[role]}</span>
              </label>
            );
          })}
          <datalist id="chain-models">
            {data.knownModels.map((m) => (
              <option value={m.spec} key={m.spec}>
                {m.label}
              </option>
            ))}
          </datalist>

          <p className="form__hint">
            The chain can call {data.chainProviders.join(' and ')} — those are the only adapters
            it registers, so a role pointed anywhere else throws on its first call, after the run
            has started. Set the thinker and the second critic together or leave both empty: they
            are checked against each other.
          </p>

          {collapse === 'same-provider' && (
            <label className="mform__ack">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                disabled={busy}
              />
              <span>
                The second critic would run on the thinker’s provider. The lab measured a
                cross-model critic union at 85.1% defect catch against 66% for a correlated one —
                accepting this is expected to cost about a fifth of the defects the chain would
                otherwise catch, and nothing at run time will tell you it happened. I am the
                administrator and I accept that.
              </span>
            </label>
          )}
          {collapse === 'same-model' && (
            <p className="form__error">
              The second critic cannot be the same model as the thinker. That is one opinion asked
              twice and paid for twice; the server refuses it and no override exists.
            </p>
          )}

          <div className="prow__actions">
            <button className="btn btn--primary btn--sm" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save models'}
            </button>
            <button
              className="btn btn--quiet btn--sm"
              type="button"
              disabled={busy}
              onClick={() => {
                setThink('');
                setEmit('');
                setCriticB('');
              }}
            >
              Clear all three
            </button>
          </div>
        </form>
      )}

      {error !== null && (
        <p className="form__error prow__wide" role="alert">
          {error}
        </p>
      )}
      {warning !== null && (
        <p className="form__error prow__wide" role="status">
          {warning}
        </p>
      )}
    </li>
  );
}

// ══ THE PAGE ═════════════════════════════════════════════════════════════════════════════

type Tab = 'people' | 'spend' | 'models';

export function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('people');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AdminUsers | null>(null);

  const isAdmin = user?.role === 'admin';

  const load = useCallback(async () => {
    setStatus('loading');
    setError(null);
    try {
      setData((await client.admin.listUsers.query()) as AdminUsers);
      setStatus('ready');
    } catch (err) {
      setError(errorMessage(err));
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return; // the courtesy gate — the server refuses regardless
    void load();
  }, [isAdmin, load]);

  if (!isAdmin) {
    return (
      <section>
        <PageHeader
          eyebrow="Admin"
          title="This area is for administrators"
          lede="Your account doesn’t have the administrator role, so the server refuses these controls."
        />
      </section>
    );
  }

  return (
    <section className="page--admin">
      <PageHeader
        eyebrow="Admin"
        title="Administration"
        lede="Who has an account and what they may do, what people have actually spent, and which models their runs are pointed at."
      />

      <nav className="admin-tabs" aria-label="Admin sections">
        {(
          [
            ['people', 'People'],
            ['spend', 'Spend'],
            ['models', 'Models'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`admin-tab${tab === id ? ' admin-tab--on' : ''}`}
            aria-current={tab === id ? 'page' : undefined}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      {status === 'loading' && <p className="page__note">Loading…</p>}

      {status === 'error' && (
        <div className="projects__state">
          <p className="form__error" role="alert">
            {error ?? 'We couldn’t load the user list.'}
          </p>
          <button className="btn btn--ghost" type="button" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      {status === 'ready' && data !== null && (
        <div className="stack">
          {tab === 'people' && (
            <div className="card">
              <h2 className="card__title">People</h2>
              <p className="card__intro">
                {data.activeAdmins === 1
                  ? 'One administrator can sign in. The server refuses anything that would leave none.'
                  : `${data.activeAdmins} administrators can sign in. The server refuses anything that would leave none.`}{' '}
                Suspending signs a person out everywhere at once and stops them signing back in;
                restoring lets them sign in again, but they must actually sign in. Limits are in
                credits per run — the system default is{' '}
                {formatCredits(data.defaultMaxRunCredits)}. What a credit costs depends on which
                providers a run ends up using, so no money figure is shown.
              </p>
              <CreatePerson reload={load} />
              <ul className="prow-list">
                {data.users.map((row) => (
                  <PersonRow key={row.user.id} row={row} data={data} reload={load} />
                ))}
              </ul>
            </div>
          )}

          {tab === 'spend' && <SpendSection />}

          {tab === 'models' && (
            <div className="card">
              <h2 className="card__title">Models</h2>
              <p className="card__intro">
                Where nothing is set, the factory’s own configuration decides — its run manifest,
                then its library defaults. This screen holds no copy of those pins on purpose: a
                second copy is a second source of truth, and the two would drift. Only the
                architect chain is settable here. Module coding reads its model from the factory’s
                manifest for the whole process and exposes no per-run override, so no choice is
                offered for it — one that could not be honoured would be a setting that lies.
              </p>
              <ul className="prow-list">
                {data.users.map((row) => (
                  <ModelRow key={row.user.id} row={row} data={data} reload={load} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
